// hooks/useServiceCounter.js
import { useState, useEffect } from 'react';
import { database, ref, set, increment, get } from '../firebase.config';

const SERVICE_NAMES = [
  "gi-pm",
  "vacum-inert",
  "filter-purify",
  "seal-gasket",
  "sensor-clean",
  "techSupport"
];

const SERVICE_LABELS = [
  "Glovebox Inspection & Preventive Maintenance",
  "Vacuum & Inert Gas System Servicing",
  "Filter & Purification Media Replacement",
  "Seal, Gasket & O-Ring Replacement",
  "Sensor Cleaning, Calibration & Validation",
  "Customization, Upgrades & Technical Support"
];

export const useServiceCounter = () => {
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [years, setYears] = useState([]);

  // Get current year
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };

  // Check if already counted in this session for a specific service
  const isFirstVisit = (serviceName) => {
    return !sessionStorage.getItem(`serviceVisitCounted_${serviceName}`);
  };

  // Increment count for a specific service
  const incrementServiceCount = async (serviceName) => {
    if (!SERVICE_NAMES.includes(serviceName)) {
      console.error("Invalid service name");
      return;
    }

    if (!isFirstVisit(serviceName)) {
      console.log(`Already counted ${serviceName} in this session`);
      return;
    }

    const year = getCurrentYear();

    try {
      const countRef = ref(database, `serviceCount/${year}/${serviceName}`);
      await set(countRef, increment(1));
      sessionStorage.setItem(`serviceVisitCounted_${serviceName}`, 'true');
      console.log(`Count incremented for ${serviceName} ${year}`);
      
      // Update local state
      setTotalCount(prev => prev + 1);
      await extractYears(); // Refresh years list
    } catch (error) {
      console.error("Error incrementing service count:", error);
    }
  };

  // Get all service counts
  const getAllServiceCounts = async () => {
    try {
      const snapshot = await get(ref(database, 'serviceCount'));
      return snapshot.exists() ? snapshot.val() : {};
    } catch (error) {
      console.error("Error getting all service counts:", error);
      return {};
    }
  };

  // Get service counts for a specific year in format [3200, 4100, 3800, 4600, 5200, 6100]
  const getServiceCountsForYear = async (year) => {
    try {
      // Initialize array with zeros for all services
      const serviceCounts = new Array(SERVICE_NAMES.length).fill(0);
      
      // Get data for the specific year
      const snapshot = await get(ref(database, `serviceCount/${year}`));
      
      if (snapshot.exists()) {
        const yearData = snapshot.val();
        
        // Fill the array with actual values in order of SERVICE_NAMES
        SERVICE_NAMES.forEach((serviceName, index) => {
          if (yearData[serviceName]) {
            serviceCounts[index] = yearData[serviceName];
          }
        });
      }
      
      return serviceCounts;
    } catch (error) {
      console.error(`Error getting service counts for ${year}:`, error);
      return new Array(SERVICE_NAMES.length).fill(0);
    }
  };

  // Extract all available years from the data
  const extractYears = async () => {
    try {
      const allCounts = await getAllServiceCounts();
      const yearList = Object.keys(allCounts).sort((a, b) => b - a);
      setYears(yearList);
      return yearList;
    } catch (error) {
      console.error("Error extracting years:", error);
      setYears([]);
      return [];
    }
  };

  // Get total count across all services and years
  const getTotalCount = async () => {
    const allCounts = await getAllServiceCounts();
    let total = 0;

    Object.values(allCounts).forEach(yearData => {
      Object.values(yearData).forEach(serviceCount => {
        total += serviceCount;
      });
    });

    return total;
  };

  // Fetch and store total count
  const fetchTotalCount = async () => {
    try {
      const total = await getTotalCount();
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching total count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize - extract years and fetch total count
  useEffect(() => {
    const initialize = async () => {
      await extractYears();
      await fetchTotalCount();
    };
    
    initialize();
  }, []);

  // Helper to increment count based on service slug/case
  const incrementServiceCountBySlug = (serviceSlug) => {
    switch (serviceSlug) {
      case "gi-pm":
        return incrementServiceCount("gi-pm");
      case "vacum-inert":
        return incrementServiceCount("vacum-inert");
      case "filter-purify":
        return incrementServiceCount("filter-purify");
      case "seal-gasket":
        return incrementServiceCount("seal-gasket");
      case "sensor-clean":
        return incrementServiceCount("sensor-clean");
      case "techSupport":
        return incrementServiceCount("techSupport");
      default:
        console.error("Unknown service slug:", serviceSlug);
    }
  };

  return {
    totalCount,
    isLoading,
    years,
    SERVICE_NAMES,
    SERVICE_LABELS,
    incrementServiceCount,
    incrementServiceCountBySlug,
    getAllServiceCounts,
    getServiceCountsForYear,
    extractYears,
    fetchTotalCount
  };
};